import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { colors } from '../constants/colors';
import { getPublicProfile, followUser, unfollowUser } from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import { parseUTC } from '../utils/date';
import type { User } from '../types';
import { ArrowLeft, Calendar, ShoppingBag, UserPlus, UserCheck } from 'lucide-react';
import { UserAvatar } from '../components/UserAvatar';

function formatDate(dateString: string | null): string {
  if (!dateString) return 'Unknown';
  const date = parseUTC(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    year: 'numeric',
  });
}

export function PublicProfilePage() {
  const { username } = useParams<{ username: string }>();
  useDocumentTitle(username ?? undefined);
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [profile, setProfile] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isFollowing, setIsFollowing] = useState(false);
  const [followerCount, setFollowerCount] = useState(0);
  const [followLoading, setFollowLoading] = useState(false);

  useEffect(() => {
    if (username) {
      loadProfile();
    }
  }, [username]);

  const loadProfile = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await getPublicProfile(username!);
      setProfile(data);
      setFollowerCount(data.followerCount ?? 0);
      setIsFollowing(data.isFollowing ?? false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load profile');
    } finally {
      setIsLoading(false);
    }
  };

  const isOwnProfile = currentUser?.username === username;

  const handleFollowToggle = async () => {
    if (!profile || followLoading) return;
    setFollowLoading(true);
    try {
      const result = isFollowing
        ? await unfollowUser(profile.id)
        : await followUser(profile.id);
      setIsFollowing(result.following);
      setFollowerCount(result.followerCount);
    } catch (err) {
      console.error('Failed to toggle follow:', err);
    } finally {
      setFollowLoading(false);
    }
  };

  if (isLoading) {
    return (
      <Layout>
        <div style={styles.container}>
          <div style={styles.loading}>Loading profile...</div>
        </div>
      </Layout>
    );
  }

  if (error || !profile) {
    return (
      <Layout>
        <div style={styles.container}>
          <div style={styles.errorContainer}>
            <h2 style={styles.errorTitle}>User not found</h2>
            <p style={styles.errorText}>
              The user {username} doesn't exist or their profile is not available.
            </p>
            <button onClick={() => navigate('/')} style={styles.backHomeButton}>
              Go Home
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div style={styles.container}>
        <button onClick={() => navigate(-1)} style={styles.backButton}>
          <ArrowLeft size={20} />
          <span>Back</span>
        </button>

        <div style={styles.profileCard}>
          {/* Avatar */}
          <div style={styles.avatarContainer}>
            <UserAvatar src={profile.avatarUrl} alt={profile.username ?? ''} size={96} />
          </div>

          {/* Profile Info */}
          <div style={styles.profileInfo}>
            <h1 style={styles.displayName}>
              {profile.displayName || profile.username}
            </h1>
            <p style={styles.username}>{profile.username}</p>

            {/* Badges */}
            <div style={styles.badges}>
              {profile.isSeller && (
                <span style={styles.sellerBadge}>
                  <ShoppingBag size={14} />
                  Seller
                </span>
              )}
            </div>

            {/* Follower count */}
            {profile.isSeller && (
              <p style={styles.followerCount}>
                {followerCount} {followerCount === 1 ? 'follower' : 'followers'}
              </p>
            )}

            {/* Bio */}
            {profile.bio && (
              <p style={styles.bio}>{profile.bio}</p>
            )}

            {/* Joined date */}
            <div style={styles.joinedDate}>
              <Calendar size={14} color={colors.gray[400]} />
              <span>Joined {formatDate(profile.createdAt)}</span>
            </div>
          </div>

          {/* Action buttons */}
          <div style={styles.actions}>
            {isOwnProfile ? (
              <button
                onClick={() => navigate('/account/edit')}
                style={styles.editButton}
              >
                Edit Profile
              </button>
            ) : (
              <>
                {profile.isSeller && currentUser && (
                  <button
                    onClick={handleFollowToggle}
                    disabled={followLoading}
                    style={isFollowing ? styles.followingButton : styles.followButton}
                  >
                    {isFollowing ? <UserCheck size={16} /> : <UserPlus size={16} />}
                    {isFollowing ? 'Following' : 'Follow'}
                  </button>
                )}
                <button
                  onClick={() => {
                    const params = new URLSearchParams({
                      recipientId: profile.id,
                      recipientName: profile.username,
                    });
                    navigate(`/inbox?${params.toString()}`);
                  }}
                  style={styles.messageButton}
                >
                  Message
                </button>
              </>
            )}
          </div>
        </div>

        {/* Placeholder sections */}
        {profile.isSeller && (
          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>Upcoming Shows</h2>
            <div style={styles.emptyState}>
              <p style={styles.emptyText}>No upcoming shows</p>
            </div>
          </div>
        )}

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Activity</h2>
          <div style={styles.emptyState}>
            <p style={styles.emptyText}>No recent activity</p>
          </div>
        </div>
      </div>
    </Layout>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    padding: '24px',
    maxWidth: '600px',
    margin: '0 auto',
  },
  loading: {
    textAlign: 'center',
    padding: '48px',
    color: colors.gray[600],
  },
  errorContainer: {
    textAlign: 'center',
    padding: '48px',
    backgroundColor: colors.white,
    borderRadius: '16px',
    border: `1px solid ${colors.gray[300]}`,
  },
  errorTitle: {
    fontSize: '20px',
    fontWeight: 600,
    color: colors.ink.black,
    marginBottom: '8px',
  },
  errorText: {
    fontSize: '14px',
    color: colors.gray[600],
    marginBottom: '24px',
  },
  backHomeButton: {
    padding: '12px 24px',
    fontSize: '14px',
    fontWeight: 600,
    color: colors.white,
    backgroundColor: colors.brand.blue,
    border: 'none',
    borderRadius: '10px',
    cursor: 'pointer',
  },
  backButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 0',
    fontSize: '14px',
    fontWeight: 500,
    color: colors.gray[600],
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    marginBottom: '16px',
  },
  profileCard: {
    backgroundColor: colors.white,
    borderRadius: '16px',
    border: `1px solid ${colors.gray[300]}`,
    padding: '32px',
    textAlign: 'center',
    marginBottom: '24px',
  },
  avatarContainer: {
    width: '120px',
    height: '120px',
    margin: '0 auto 16px',
  },
  avatar: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    borderRadius: '50%',
  },
  avatarPlaceholder: {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '48px',
    fontWeight: 600,
    backgroundColor: colors.brand.blue,
    color: colors.white,
    borderRadius: '50%',
  },
  profileInfo: {
    marginBottom: '20px',
  },
  displayName: {
    fontSize: '24px',
    fontWeight: 700,
    color: colors.ink.black,
    marginBottom: '4px',
  },
  username: {
    fontSize: '16px',
    color: colors.gray[600],
    marginBottom: '12px',
  },
  badges: {
    display: 'flex',
    justifyContent: 'center',
    gap: '8px',
    marginBottom: '16px',
  },
  sellerBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 12px',
    fontSize: '13px',
    fontWeight: 600,
    color: colors.accent.purple,
    backgroundColor: '#EDE9FE',
    borderRadius: '100px',
  },
  bio: {
    fontSize: '15px',
    color: colors.gray[700],
    lineHeight: 1.5,
    marginBottom: '16px',
    maxWidth: '400px',
    margin: '0 auto 16px',
  },
  joinedDate: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    fontSize: '13px',
    color: colors.gray[400],
  },
  actions: {
    display: 'flex',
    justifyContent: 'center',
    gap: '12px',
  },
  editButton: {
    padding: '10px 24px',
    fontSize: '14px',
    fontWeight: 600,
    color: colors.brand.blue,
    backgroundColor: colors.brand.blue15,
    border: 'none',
    borderRadius: '10px',
    cursor: 'pointer',
  },
  followButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '10px 24px',
    fontSize: '14px',
    fontWeight: 600,
    color: colors.white,
    backgroundColor: colors.brand.blue,
    border: 'none',
    borderRadius: '10px',
    cursor: 'pointer',
  },
  followingButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '10px 24px',
    fontSize: '14px',
    fontWeight: 600,
    color: colors.brand.blue,
    backgroundColor: colors.brand.blue15,
    border: `1px solid ${colors.brand.blue}`,
    borderRadius: '10px',
    cursor: 'pointer',
  },
  followerCount: {
    fontSize: '14px',
    fontWeight: 600,
    color: colors.gray[600],
    marginBottom: '8px',
  },
  messageButton: {
    padding: '10px 24px',
    fontSize: '14px',
    fontWeight: 600,
    color: colors.white,
    backgroundColor: colors.brand.blue,
    border: 'none',
    borderRadius: '10px',
    cursor: 'pointer',
  },
  section: {
    backgroundColor: colors.white,
    borderRadius: '16px',
    border: `1px solid ${colors.gray[300]}`,
    padding: '20px',
    marginBottom: '16px',
  },
  sectionTitle: {
    fontSize: '16px',
    fontWeight: 700,
    color: colors.ink.black,
    marginBottom: '16px',
  },
  emptyState: {
    padding: '24px',
    textAlign: 'center',
  },
  emptyText: {
    fontSize: '14px',
    color: colors.gray[400],
  },
};
